/* eslint-disable @typescript-eslint/only-throw-error */
import { AnnaError } from "@anna-money/anna-web-lib/utils";

import { makeAutoObservable } from "mobx";

import { Config } from "config/config";
import type { AddressFormValue } from "helpers/forms/addressTypes";
import { directorFormToDirectorUpdateData } from "helpers/forms/director";
import type { DirectorStepFormValues } from "helpers/forms/directorTypes";
import type { CompanyStore } from "services/company/companyStore";
import { type AcspFormOutcome, getAcspFormOutcome } from "services/director/acspFormOutcome";
import {
  DIRECTOR_CH_VERIFICATION_STATUS,
  DirectorCHPersonalCodeUpdateData,
  DirectorCreateData,
  DirectorData,
  DirectorDriversLicenceStateUpdateData,
  DirectorPreviousAddressUpdateData,
  DirectorSharesUpdateData,
  DirectorSignificantUpdateData,
  DirectorUpdateData,
  MainDirectorCreateData,
} from "services/director/directorTypes";
import type { DirectorVerificationStatusData } from "services/directorCodeModal/directorCodeModalTypes";
import { DirectorPositionStore } from "services/directorPosition/directorPositionStore";
import { FormError } from "types/errors";
import { type FormSubmitResult } from "types/form";
import { DirectorAddressKey, DirectorAddressStore } from "./directorAddressStore";
import { DirectorClient } from "./directorClient";

interface DirectorState {
  directors: DirectorData[];
  mainDirector?: DirectorData;
  mainDirectorAcspRequestedAt?: number;
}

export enum DirectorEntity {
  Intro = "Intro",
  FirstTimeDirector = "FirstTimeDirector",
  Address = "Address",
  PreviousAddress = "PreviousAddress",
  Shares = "Shares",
}

class NoMainDirectorError extends AnnaError {
  constructor(message: string = "No main director in the store") {
    super(message);
  }
}

export class DirectorStore {
  private readonly _state: DirectorState = { directors: [] };

  constructor(
    private readonly _directorClient: DirectorClient,
    private readonly _companyStore: CompanyStore,
    private readonly _addressStore: DirectorAddressStore,
    private readonly _directorPositionsStore: DirectorPositionStore,
    private readonly _config: Config,
  ) {
    makeAutoObservable(this);
  }

  get directors(): DirectorData[] {
    return this._state.directors;
  }

  private set directors(value: DirectorData[]) {
    this._state.directors = value;
  }

  get mainDirector(): DirectorData | undefined {
    return this._state.mainDirector;
  }

  private set mainDirector(value: DirectorData | undefined) {
    this._state.mainDirector = value;
  }

  /** When this session asked for the main director's ACSP form, as a timestamp in ms */
  get mainDirectorAcspRequestedAt(): number | undefined {
    return this._state.mainDirectorAcspRequestedAt;
  }

  async init(): Promise<void> {
    await this._getDirectorList();

    for (const director of this.directors) {
      await this._addressStore.loadForDirector(director.id);
      if (!this._config.isAU) {
        await this._addressStore.loadForDirector(director.id, DirectorAddressKey.Previous);
        if (director.isCurrentAddressLessThanOneYear != null) {
          this._addressStore.setisCurrentAddressLessThanOneYear(director.id, director.isCurrentAddressLessThanOneYear);
        }
      }
    }
  }

  getMainDirector(): DirectorData {
    if (!this.mainDirector) {
      throw new NoMainDirectorError();
    }

    return this.mainDirector;
  }

  tryGetDirectorById(id: string): DirectorData | undefined {
    return this._state.directors.find((director) => director.id === id);
  }

  getDirectorById(id: string): DirectorData {
    const director = this.tryGetDirectorById(id);

    if (!director) {
      throw new AnnaError(`No director with the id ${id}`);
    }

    return director;
  }

  async getDirectorCHVerificationStatus(): Promise<DIRECTOR_CH_VERIFICATION_STATUS | undefined> {
    await this._getDirectorList();
    return this.mainDirector?.identityVerificationStatus || undefined;
  }

  async refreshDirectors(): Promise<void> {
    await this._getDirectorList();
  }

  async getMainDirectorAcspFormOutcome(): Promise<AcspFormOutcome> {
    await this._getDirectorList();
    return getAcspFormOutcome(this.mainDirector);
  }

  getNonDirectors(): DirectorData[] {
    return this.directors.filter((director) => director.id !== this.mainDirector?.id);
  }

  checkAllDirectorDetailExists(): boolean {
    return this.directors.every((director) => {
      const hasAddress = this._addressStore.hasAddressForDirector(director.id);
      if (this._config.isAU) {
        return hasAddress;
      }
      const hasPreviousAddress = this._addressStore.hasPreviousAddressForDirector(director.id);
      return hasAddress && hasPreviousAddress;
    });
  }

  checkSharesExist(): boolean {
    const { company } = this._companyStore;

    // Check if company has valid total shares and capital
    if (!company?.totalSharesNum || !company?.totalCapitalGbp) {
      return false;
    }

    // Check if all shareholders have valid shares
    return this.directors.every((director) => {
      // If director is a shareholder, they must have ownsSharesNum > 0
      if (director.isShareholder) {
        return director.ownsSharesNum && director.ownsSharesNum > 0;
      }
      // Non-shareholders don't need shares
      return true;
    });
  }

  // method for update only main director during passing through wizard
  async updateMainDirector(formValues: DirectorStepFormValues, entity: DirectorEntity): Promise<FormSubmitResult> {
    // skip the API call for Intro step
    if (entity === DirectorEntity.Intro) {
      return undefined;
    }
    if (!this._companyStore.company) {
      await this._companyStore.createCompany();
    }

    if (!this.mainDirector) {
      await this._createMainDirector();
    }

    switch (entity) {
      case DirectorEntity.Address: {
        const mainDirector = this.getMainDirector();
        const isCurrentAddressLessThanOneYear = Boolean(formValues.isCurrentAddressLessThanOneYear);
        if (isCurrentAddressLessThanOneYear) {
          this._addressStore.setisCurrentAddressLessThanOneYear(mainDirector.id, true);
        } else {
          this._addressStore.clearPreviousAddressForDirector(mainDirector.id);
        }
        this.mainDirector = await this._directorClient.updateDirector(
          mainDirector.id,
          new DirectorPreviousAddressUpdateData({ isCurrentAddressLessThanOneYear }),
        );
        return await this.updateAddress(mainDirector.id, formValues.address);
      }
      case DirectorEntity.PreviousAddress: {
        const mainDirector = this.getMainDirector();
        this.mainDirector = await this._directorClient.updateDirector(
          mainDirector.id,
          new DirectorPreviousAddressUpdateData({ previousAddressInUk: formValues.previousAddressInUk }),
        );
        if (formValues.previousAddressInUk === false) {
          this._addressStore.clearPreviousAddressForDirector(mainDirector.id);
        } else {
          await this._updateAddress(mainDirector.id, formValues.previousAddress, DirectorAddressKey.Previous);
        }
        return undefined;
      }
      case DirectorEntity.FirstTimeDirector: {
        await this._companyStore.updateAbnFirstTimeDirector(Boolean(formValues.abnFirstTimeDirector));
        return undefined;
      }
      default:
        await this._updateMainDirector(formValues);
    }
  }

  async updateAddress(directorId: string, address?: AddressFormValue): Promise<FormSubmitResult> {
    await this._updateAddress(directorId, address);
  }

  async createDirector(data: DirectorCreateData): Promise<string> {
    const directorData = await this._directorClient.createDirector(data);

    await this._getDirectorList();

    return directorData.id;
  }

  async updateDirector(directorId: string, data: DirectorUpdateData): Promise<void> {
    await this._directorClient.updateDirector(directorId, data);

    await this._getDirectorList();
  }

  async deleteDirector(directorId: string): Promise<void> {
    await this._directorClient.deleteDirector(directorId);

    await this._getDirectorList();
  }

  async updateShares(directorId: string, shares?: number): Promise<void> {
    await this._directorClient.updateDirector(
      directorId,
      new DirectorSharesUpdateData({ ownsSharesNum: shares ?? null }),
    );

    await this._getDirectorList();
  }

  async updateSignificantControl(
    directorId: string,
    isSignificant: boolean,
    hasRightToAppointNewDirectors?: boolean,
    hasSignificantControlOutsideOfShares?: boolean,
  ): Promise<void> {
    await this._directorClient.updateDirector(
      directorId,
      new DirectorSignificantUpdateData({
        isPersonWithSignificantControl: isSignificant,
        hasRightToAppointNewDirectors: hasRightToAppointNewDirectors ?? false,
        hasSignificantControlOutsideOfShares: hasSignificantControlOutsideOfShares ?? false,
      }),
    );

    await this._getDirectorList();
  }

  async updateDriversLicenceState(directorId: string, drivingLicenceStateOfIssue: string): Promise<void> {
    await this._directorClient.updateDirector(
      directorId,
      new DirectorDriversLicenceStateUpdateData({ drivingLicenceStateOfIssue }),
    );
  }

  async updateCHPersonalCode(directorId: string, chPersonalCode: string): Promise<void> {
    await this._directorClient.updateDirector(directorId, new DirectorCHPersonalCodeUpdateData({ chPersonalCode }));
    await this._getDirectorList();
  }

  async submitMainDirectorACSPVerification(): Promise<void> {
    await this._directorClient.submitACSPVerification(this.getMainDirector().id);
    this._state.mainDirectorAcspRequestedAt = Date.now();
    // The request went through: a list that fails to reload must not report it as failed - the
    // processing screen counts its wait from the time above and its polling reloads the list anyway
    try {
      await this._getDirectorList();
    } catch (error) {
      console.error("Failed to reload directors after the ACSP request", error);
    }
  }

  async startSecondaryDirectorVerification(): Promise<void> {
    await this._directorClient.startSecondaryDirectorVerification();
  }

  async requestDirectorVerification(directorId: string): Promise<void> {
    await this._directorClient.requestDirectorVerification(directorId);
    await this._getDirectorList();
  }

  async resetDirectorVerification(directorId: string): Promise<void> {
    await this._directorClient.resetDirectorVerification(directorId);
    // The backend clears the stale personal code and status, so the cards must be reloaded
    await this._getDirectorList();
  }

  async getDirectorVerificationStatus(directorId: string): Promise<DIRECTOR_CH_VERIFICATION_STATUS | undefined> {
    await this._getDirectorList();
    const director = this.tryGetDirectorById(directorId);
    return director?.identityVerificationStatus;
  }

  private async _getDirectorList(): Promise<void> {
    const { company } = this._companyStore;

    if (!company) {
      return;
    }

    this.directors = await this._directorClient.getDirectorList();

    for (const director of this.directors) {
      if (director.id === company.registeredWithDirectorId) {
        this.mainDirector = director;
      }
      // Sync position from API data
      this._directorPositionsStore.syncFromDirector(director.id, director.isDirector, director.isShareholder);
    }
  }

  async getSecondaryDirectorVerificationStatus(
    directorId: string,
  ): Promise<DirectorVerificationStatusData | undefined> {
    return await this._directorClient.getDirectorVerificationStatus(directorId);
  }

  private async _createMainDirector(): Promise<void> {
    this.mainDirector = await this._directorClient.createDirector(
      new MainDirectorCreateData({ companyId: this._companyStore.getCompany().id }),
    );
  }

  private async _updateMainDirector(formValues: DirectorStepFormValues): Promise<void> {
    this.mainDirector = await this._directorClient.updateDirector(
      this.getMainDirector().id,
      directorFormToDirectorUpdateData(formValues),
    );
  }

  private async _updateAddress(
    directorId: string,
    address?: AddressFormValue,
    addressKey?: DirectorAddressKey,
  ): Promise<void> {
    if (!address) {
      throw new FormError("Something went wrong, no address in the form");
    }

    await this._addressStore.updateForDirector(directorId, address, addressKey);

    await this._getDirectorList();
  }
}
